/* Mangatrai editorial theme scripts: reveal-on-scroll, <md-carousel>, <md-header>, <md-search>, <md-finder>. No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Reveal on scroll ---------- */
  let revealObserver = null;
  function initReveal(root = document) {
    const els = root.querySelectorAll('[data-md-reveal]:not(.is-in)');
    if (!els.length) return;
    if (!document.documentElement.classList.contains('md-anim') || designMode || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    root.querySelectorAll('[data-md-stagger]').forEach((group) => {
      group.querySelectorAll(':scope > [data-md-reveal], :scope > * > [data-md-reveal]').forEach((child, i) => {
        child.style.setProperty('--md-d', `${Math.min(i, 8) * 0.07}s`);
      });
    });
    els.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- <md-carousel> : grid that becomes a scroll-snap slider per breakpoint ---------- */
  class MdCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.md-track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-md-prev]');
      this.next = this.querySelector('[data-md-next]');
      this.dots = this.querySelector('.md-car-dots');
      this.autoplay = parseFloat(this.dataset.autoplay || '0');
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.ro = new ResizeObserver(this.update);
      this.ro.observe(this.track);
      ['mouseenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => (this.paused = true), { passive: true }));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));
      this.update();
    }
    disconnectedCallback() {
      this.ro && this.ro.disconnect();
      clearInterval(this.timer);
    }
    get sliding() {
      return getComputedStyle(this.track).display === 'flex' && this.track.scrollWidth > this.track.clientWidth + 2;
    }
    pageCount() {
      return Math.max(1, Math.ceil((this.track.scrollWidth - 2) / this.track.clientWidth));
    }
    update() {
      const sliding = this.sliding;
      this.classList.toggle('is-sliding', sliding);
      clearInterval(this.timer);
      if (!sliding) return;
      this.placeArrows();
      if (this.dots) {
        const n = this.pageCount();
        if (this.dots.children.length !== n) {
          this.dots.innerHTML = '';
          for (let i = 0; i < n; i++) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'md-car-dot';
            b.setAttribute('aria-label', `${this.dataset.slideLabel || 'Slide'} ${i + 1}`);
            b.addEventListener('click', () => this.track.scrollTo({ left: i * this.track.clientWidth, behavior: this.behavior }));
            this.dots.appendChild(b);
          }
        }
      }
      this.onScroll();
      if (this.autoplay > 0 && !reduceMotion.matches && !designMode) {
        this.timer = setInterval(() => {
          if (this.paused || document.hidden) return;
          const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
          atEnd ? this.track.scrollTo({ left: 0, behavior: this.behavior }) : this.go(1);
        }, this.autoplay * 1000);
      }
    }
    // Side arrows sit on the vertical middle of the first card's image (or the card itself when it has none)
    placeArrows() {
      if (this.dataset.arrowsPos !== 'sides') return;
      const item = this.track.firstElementChild;
      if (!item) return;
      const media = item.querySelector('.md-media, .card__media, video') || item;
      const r = media.getBoundingClientRect();
      if (!r.height) return;
      this.style.setProperty('--md-arrow-y', `${Math.round(r.top - this.getBoundingClientRect().top + r.height / 2)}px`);
    }
    get behavior() {
      return reduceMotion.matches ? 'auto' : 'smooth';
    }
    go(dir) {
      const item = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: dir * step * perView, behavior: this.behavior });
    }
    onScroll() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max - 2;
      if (this.dots && this.dots.children.length) {
        const i = x >= max - 2 ? this.dots.children.length - 1 : Math.round(x / this.track.clientWidth);
        [...this.dots.children].forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      }
    }
  }
  if (!customElements.get('md-carousel')) customElements.define('md-carousel', MdCarousel);

  /* ---------- Scroll lock helper ---------- */
  let locks = 0;
  const lock = (on) => {
    locks = Math.max(0, locks + (on ? 1 : -1));
    document.documentElement.classList.toggle('md-lock', locks > 0);
  };
  const focusables = (el) =>
    [...el.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')].filter(
      (n) => n.offsetParent !== null
    );
  function trapTab(e, panel) {
    if (e.key !== 'Tab') return;
    const f = focusables(panel);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- <md-header> ---------- */
  class MdHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.sticky = this.dataset.sticky || 'none';
      this.overlay = this.dataset.overlay === 'true';
      this.lastY = window.scrollY;
      this.section.classList.add('md-header-section');
      this.section.classList.toggle('md-sticky', this.sticky !== 'none');
      this.section.classList.toggle('md-sticky--menu', this.sticky === 'menu');
      this.section.classList.toggle('is-overlay', this.overlay);
      this.measure = this.measure.bind(this);
      this.onScroll = this.onScroll.bind(this);
      new ResizeObserver(this.measure).observe(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('mouseenter', () => this.overlay && this.section.classList.add('is-solid'));
      this.addEventListener('mouseleave', () => this.overlay && this.onScroll());
      this.addEventListener('focusin', () => this.overlay && this.section.classList.add('is-solid'));

      this.drawer = this.querySelector('.md-drawer');
      this.querySelectorAll('[data-md-drawer-open]').forEach((b) => b.addEventListener('click', () => this.openDrawer(b)));
      this.querySelectorAll('[data-md-drawer-close]').forEach((b) => b.addEventListener('click', () => this.closeDrawer()));
      this.drawer &&
        this.drawer.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeDrawer();
          trapTab(e, this.drawer.querySelector('.md-drawer__panel'));
        });
      this.measure();
      this.onScroll();
    }
    measure() {
      const h = Math.round(this.getBoundingClientRect().height);
      this.section.style.setProperty('--md-header-h', `${h}px`);
      document.documentElement.style.setProperty('--md-header-h', `${h}px`);
      // Height above the menu row (what scrolls away in "Menu bar only" sticky mode)
      const nav = this.querySelector('.md-nav');
      const top = this.querySelector('.md-header__top');
      const hTop = this.getBoundingClientRect().top;
      if (this.querySelector('.md-header--inline')) this.section.style.setProperty('--md-top-h', '0px');
      else if (nav && nav.offsetParent) this.section.style.setProperty('--md-top-h', `${Math.max(0, Math.round(nav.getBoundingClientRect().top - hTop))}px`);
      else if (top) this.section.style.setProperty('--md-top-h', `${Math.round(top.getBoundingClientRect().height)}px`);
      const bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
    }
    onScroll() {
      const y = window.scrollY;
      const scrolled = y > 8;
      this.section.classList.toggle('is-scrolled', scrolled && this.sticky !== 'none');
      if (this.overlay) this.section.classList.toggle('is-solid', scrolled || (this.drawer && this.drawer.classList.contains('is-open')) || this.matches(':hover'));
      if (this.sticky === 'scroll-up') {
        const hide = y > this.offsetHeight + 60 && y > this.lastY && !document.documentElement.classList.contains('md-lock');
        this.section.classList.toggle('is-hidden', hide);
      }
      this.lastY = y;
    }
    openDrawer(trigger) {
      if (!this.drawer) return;
      this.trigger = trigger;
      this.drawer.classList.add('is-open');
      this.drawer.removeAttribute('inert');
      this.drawer.setAttribute('aria-hidden', 'false');
      trigger && trigger.setAttribute('aria-expanded', 'true');
      lock(true);
      const first = this.drawer.querySelector('.md-drawer__panel [data-md-drawer-close]');
      setTimeout(() => first && first.focus(), 50);
    }
    closeDrawer() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      this.trigger && this.trigger.setAttribute('aria-expanded', 'false');
      lock(false);
      this.trigger && this.trigger.focus();
    }
  }
  if (!customElements.get('md-header')) customElements.define('md-header', MdHeader);

  /* ---------- <md-search> : popup with suggestions, featured products and live results ---------- */
  class MdSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-md-results]');
      this.clearBtn = this.querySelector('[data-md-search-clear]');
      this.status = this.querySelector('[data-md-status]');
      this.panel = this.querySelector('.md-search__panel');
      this.cache = new Map();
      this.sectionId = this.dataset.sectionId || 'md-predictive-search';
      this.limit = this.dataset.limit || '6';
      this.onInput = this.debounce(this.onInput.bind(this), 260);
      document.querySelectorAll('[data-md-search-open]').forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(b);
        })
      );
      this.querySelectorAll('[data-md-search-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.input.addEventListener('input', () => {
        this.clearBtn.hidden = !this.input.value;
        this.onInput();
      });
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.clearBtn.hidden = true;
        this.setState('idle');
        this.input.focus();
      });
      this.querySelectorAll('[data-md-term]').forEach((chip) =>
        chip.addEventListener('click', (e) => {
          e.preventDefault();
          this.input.value = chip.dataset.mdTerm;
          this.clearBtn.hidden = false;
          this.search(this.input.value.trim());
          this.input.focus();
        })
      );
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') this.moveSelection(e);
        trapTab(e, this.panel);
      });
    }
    debounce(fn, ms) {
      let t;
      return (...a) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...a), ms);
      };
    }
    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      lock(true);
      setTimeout(() => this.input.focus(), 60);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      lock(false);
      this.abort && this.abort.abort();
      this.trigger && this.trigger.focus && this.trigger.focus();
    }
    setState(state) {
      this.dataset.state = state;
    }
    onInput() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.setState('idle');
        return;
      }
      this.search(q);
    }
    async search(q) {
      if (this.cache.has(q)) return this.render(this.cache.get(q), q);
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      this.setState('loading');
      const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      const url = `${base}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=${this.limit}&resources[limit_scope]=each&section_id=${this.sectionId}`;
      try {
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const html = (doc.querySelector('[data-md-ps]') || doc.body).innerHTML;
        this.cache.set(q, html);
        this.render(html, q);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.results.innerHTML = `<p class="md-search__state">${this.dataset.errorText || 'Search is unavailable right now.'}</p>`;
        this.setState('results');
      }
    }
    render(html, q) {
      if (this.input.value.trim() !== q) return;
      this.results.innerHTML = html;
      this.setState('results');
      const count = this.results.querySelectorAll('.md-row').length;
      if (this.status) this.status.textContent = count ? `${count} results` : this.dataset.emptyText || 'No results';
    }
    moveSelection(e) {
      const rows = [...this.querySelectorAll(this.dataset.state === 'results' ? '[data-md-results] .md-row' : '.md-search__idle .md-row')];
      if (!rows.length) return;
      e.preventDefault();
      const i = rows.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? rows[(i + 1) % rows.length] : rows[(i - 1 + rows.length) % rows.length];
      next.focus();
    }
  }
  if (!customElements.get('md-search')) customElements.define('md-search', MdSearch);

  /* ---------- <md-finder> : pick chips, CTA follows the most specific choice ---------- */
  class MdFinder extends HTMLElement {
    connectedCallback() {
      this.cta = this.querySelector('[data-md-finder-cta]');
      this.summary = this.querySelector('[data-md-finder-summary]');
      this.defaultHref = this.cta ? this.cta.getAttribute('href') : '#';
      this.emptyText = this.summary ? this.summary.textContent : '';
      this.querySelectorAll('[data-md-chip]').forEach((chip) =>
        chip.addEventListener('click', () => {
          const row = chip.closest('[data-md-row]');
          const on = chip.getAttribute('aria-pressed') !== 'true';
          row.querySelectorAll('[data-md-chip]').forEach((c) => c.setAttribute('aria-pressed', 'false'));
          chip.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on) this.lastHref = chip.dataset.href || '';
          this.refresh();
        })
      );
    }
    refresh() {
      const picked = [...this.querySelectorAll('[data-md-chip][aria-pressed="true"]')];
      const withLink = picked.filter((c) => c.dataset.href);
      const href = withLink.length ? withLink[withLink.length - 1].dataset.href : this.defaultHref;
      if (this.cta) this.cta.setAttribute('href', href || this.defaultHref);
      if (this.summary) {
        this.summary.textContent = picked.length
          ? picked.map((c) => c.textContent.trim()).join(' · ') + (this.dataset.suffix ? ` — ${this.dataset.suffix}` : '')
          : this.emptyText;
      }
    }
  }
  if (!customElements.get('md-finder')) customElements.define('md-finder', MdFinder);

  /* ---------- Boot ---------- */
  window.mdReady = true;
  const boot = () => initReveal();
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();
